import {
  DataInterval,
  ReportTooltipTemplateContext,
  ReportsTranslations,
  DatasetBase,
  ReportDateFilter,
  ReportTooltipNormalizedParams,
} from '@core/reports-types';
import { datasetTooltipFormatterOptions } from '@core/reports-util';
import { SupportedNumberFormat, valueFormatter } from '@core/utils-legacy';

import { formatDateWithInterval } from './date-format';

const getParamValue = <T = string | number>(param: ReportTooltipNormalizedParams[0]) => {
  const value = Array.isArray(param.value) ? param.value : [param.value];
  return <T>value[param.encode!.y[0]];
};

const getFooterText = (
  dataset: DatasetBase,
  translations: ReportsTranslations,
  params: ReportTooltipNormalizedParams,
) => {
  if (dataset.isSingleDimension || params.length === 1) {
    return {
      footerPrimaryText: undefined,
      footerSecondaryText: undefined,
    };
  } else {
    const count = params.reduce((acc: number, param) => acc + getParamValue<number>(param), 0);
    const formatted = valueFormatter(
      count,
      datasetTooltipFormatterOptions(dataset),
      translations.i18n,
      {
        number: { format: SupportedNumberFormat.IntlStandard },
      },
    );

    return {
      footerPrimaryText: translations.i18n.translate('tlr.tooltips.total'),
      footerSecondaryText: `${formatted} ${translations.i18n.translate(
        `tlr.resource_type_unit.${dataset.metric.resourceId}`,
        { count },
      )}`,
    };
  }
};

export const trendTooltipContextFactory = (
  component: { interval: DataInterval; dateFilter: ReportDateFilter; clampEndToToday?: boolean },
  dataset: DatasetBase,
  translations: ReportsTranslations,
  params: ReportTooltipNormalizedParams,
): ReportTooltipTemplateContext => {
  const nonEmpty = params.filter(p => {
    const value = Array.isArray(p.value) ? p.value : [p.value];
    return <number>value[p.encode!.y[0]] > 0;
  });

  const { primary } = formatDateWithInterval(
    (<any>params[0]).axisValueLabel,
    component.dateFilter,
    component.interval,
    component.clampEndToToday,
  );

  if (nonEmpty.length === 0) {
    return {
      headerText: dataset.metric.dataLabel,

      items: [
        {
          label: primary,
          color: dataset.isSingleDimension ? <string>params[0].color : 'transparent',
          value: '-',
        },
      ],
    };
  }

  const { footerPrimaryText, footerSecondaryText } = getFooterText(dataset, translations, nonEmpty);

  const items = nonEmpty.reduce(
    (acc, param) => {
      acc.push({
        id: param.seriesId,
        label: dataset.isSingleDimension ? primary : dataset.getDimensionNameById(param.seriesId!),
        value: valueFormatter(
          getParamValue(param),
          datasetTooltipFormatterOptions(dataset),
          translations.i18n,
          {
            number: { format: SupportedNumberFormat.IntlStandard },
          },
        ),
        color: <string>param.color,
      });

      return acc;
    },
    <ReportTooltipTemplateContext['items']>[],
  );

  return {
    headerText: dataset.isSingleDimension ? dataset.metric.dataLabel : primary,
    footerPrimaryText,
    footerSecondaryText,
    items,
  };
};
